import { useCallback, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { ROUTE_ORDERS, ROUTE_PURCHASE_ORDERS, ROUTE_RECEIVABLE, buildNavPathTrader } from '@klear/constants'
import { Wizard } from '@klear/ui/wizard/wizard'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { enhanceReturnUrl, returnUrlHasPathSegment, useReturnUrlExit } from '@klear/ui/wizard/return-url'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import isFinite from 'lodash/isFinite'
import { PurchaseOrderFlow } from '@/app/purchase-order/workflow/purchase-order-step-group'
import { STEP_ID as PAYABLE_STEP_ID } from '@klear/payable/constants'
import { STEP_ID as RECEIVABLE_STEP_ID } from '@/app/receivable/workflow/receivable-steps'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function PurchaseOrderCreateWizardPage() {
  useDocumentTitle('New Purchase Order')
  const { traderId } = useParams<{ traderId?: string }>()
  const [searchParams] = useSearchParams()
  const tid = toInteger(traderId)
  const orderIdRaw = toInteger(searchParams.get('orderId') ?? '')
  const orderId = isFinite(orderIdRaw) && orderIdRaw > 0 ? orderIdRaw : null
  const defaultExit = buildNavPathTrader(toString(tid), ROUTE_ORDERS)
  const { exit, exitAfterSave } = useReturnUrlExit(defaultExit)
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const [dirty, setDirty] = useState(false)
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: dirty })
  const guardedExit = useMemo(() => guardExit(exit), [guardExit, exit])

  const onComplete = useCallback(
    ({ purchaseOrderId }: { purchaseOrderId: number }) => {
      exitAfterSave((ret) => {
        const isReceivable = returnUrlHasPathSegment(ret, ROUTE_RECEIVABLE)
        return enhanceReturnUrl(ret, {
          query: { purchaseOrderId: toString(purchaseOrderId) },
          hash: isReceivable ? RECEIVABLE_STEP_ID.INVOICE : PAYABLE_STEP_ID.OPERATIONAL
        })
      })
    },
    [exitAfterSave]
  )

  if (orderId == null) {
    return <div className="p-6 text-sm text-muted-foreground">Missing orderId for purchase order create.</div>
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {LeaveConfirmDialog}
      <Wizard
        name="Purchase order"
        subtitle={nav?.steps.find((s) => s.id === nav.activeStepId)?.label ?? 'Create'}
        steps={nav?.steps ?? []}
        activeStepId={nav?.activeStepId ?? ''}
        onStepSelect={nav?.onStepSelect}
        isStepDisabled={nav?.isStepDisabled}
        onBack={guardedExit}
        backLabel="Exit"
      >
        <PurchaseOrderFlow
          mode="create"
          traderId={tid}
          orderId={orderId}
          cancelOnFirstStep
          onNavStateChange={setNav}
          onComplete={onComplete}
          onCancel={guardedExit}
          onDirtyChange={setDirty}
        />
      </Wizard>
    </div>
  )
}

export const purchaseOrderCreatePath = (traderId: string | number, orderId: number) =>
  `${buildNavPathTrader(toString(traderId), ROUTE_PURCHASE_ORDERS, 'new')}?orderId=${orderId}`
